import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
// import React, { forwardRef } from "react"; used if you want to print Article by clicking button present on ArticleCard component
import SEO from '../SEO'
import { generateArticleSchema } from '../../services/schemaGenerators'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import PrintRoundedIcon from '@mui/icons-material/PrintRounded'
import MailRoundedIcon from '@mui/icons-material/MailRounded'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import ReportRoundedIcon from '@mui/icons-material/ReportRounded'
import SortByAlphaRoundedIcon from '@mui/icons-material/SortByAlphaRounded'
import RestoreRoundedIcon from '@mui/icons-material/RestoreRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import BetaVersionNotice from '../BetaVersionNotice'
import {
  Box,
  IconButton,
  Typography,
  Paper,
  Skeleton,
  Fab,
  Zoom,
  useTheme,
  useMediaQuery,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material'
import _ from 'lodash'
// spread operator (...) might creates a shallow copy of the array. When dealing with nested objects, such as the data array within the content array, this can lead to unintentional mutations of the original data structure, for reference: sortDefinitions, to create a deep copy of the article.content we can use a utility function to deep clone objects, such as lodash's cloneDeep method.

import {
  handleEmail,
  handleOpenInNewTab,
} from '../../services/articleCardHandlers'

import {
  parseHtmlSanitizeAddTargetToLinks,
  deepCopy,
} from '../../services/utils'
import { fetchArticleBySlug } from '../../services/articleAPISevicesUsingFetch'
import apiService from '../../services/ApiService'
import NestedOrderedList from '../NestedOrderedList'
import FeedbackForm from '../FeedbackForm'
import CustomTable from '../CustomTable'
import { Enhanced404Handler } from '../Error404'
import { ShareRounded } from '@mui/icons-material'
// Module-level cache
const contentCache = {}

// To enable the PrintRoundedIcon button in the ArticleCard component to print the Article component within it, you need to pass a reference of the Article component to the print function. Utilize a ref to get the Article component's HTML content.
const Article =
  // forwardRef(
  ({
    article,
    searchQuery,
    showSnackbar,
    isInModal = false, // Prop to indicate if component is inside a modal
    parentContainerRef = null, // Reference to parent container for scrolling in modal mode
  }) =>
    // ,ref
    {
      const [articleContent, setArticleContent] = useState(null)
      const [loading, setLoading] = useState(true)
      const [error, setError] = useState(null)
      const [isFeedbackFormOpen, setIsFeedbackFormOpen] = useState(false)
      const [showBackToTop, setShowBackToTop] = useState(false)
      const componentRef = useRef()
      const fetchedRef = useRef(false) // Track fetch state to prevent duplicate calls

      // we need to sort lexicographically, all the definitions of Article 1 of CIT law
      const [parsedContent, setParsedContent] = useState(null)
      const [sortedContent, setSortedContent] = useState(null)
      // const [isSorted, setIsSorted] = useState(false);
      const [contentHtml, setContentHtml] = useState(null)
      const [gtlNotesContent, setGtlNotesContent] = useState(null)
      const [gtlNotesTitle, setGtlNotesTitle] = useState('GTL Notes')
      const [isAccordionExpanded, setIsAccordionExpanded] = useState(false)

      // Theme colors
      const themeColors = {
        primary: '#232536',
        secondary: '#ffcf51',
        hover: '#3a3e5a',
      }
      const theme = useTheme()
      const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'))
      const isMobileScreen = useMediaQuery(theme.breakpoints.down('sm'))
      const buttonBorderRadius = '28px'
      // Decide which container to use for scroll detection and scrollToTop action
      const scrollContainer = isInModal ? parentContainerRef : window

      // Show back to top button when scrolled down
      useEffect(() => {
        if (!scrollContainer) return

        const handleScroll = () => {
          // For window, use scrollY; for element, use scrollTop
          const scrollPosition = isInModal
            ? scrollContainer.current?.scrollTop
            : window.scrollY

          if (scrollPosition > 300) {
            setShowBackToTop(true)
          } else {
            setShowBackToTop(false)
          }
        }

        // Add scroll listener to the appropriate container
        if (isInModal && scrollContainer.current) {
          scrollContainer.current.addEventListener('scroll', handleScroll)
        } else {
          window.addEventListener('scroll', handleScroll)
        }

        return () => {
          // Clean up scroll listener
          if (isInModal && scrollContainer.current) {
            scrollContainer.current.removeEventListener('scroll', handleScroll)
          } else {
            window.removeEventListener('scroll', handleScroll)
          }
        }
      }, [isInModal, scrollContainer])

      // Scroll to top function
      const scrollToTop = useCallback(() => {
        if (isInModal && parentContainerRef?.current) {
          parentContainerRef.current.scrollTo({
            top: 0,
            behavior: 'smooth',
          })
        } else {
          window.scrollTo({
            top: 0,
            behavior: 'smooth',
          })
        }
      }, [isInModal, parentContainerRef])
      const extractGtlNotes = useCallback((htmlContent) => {
        try {
          if (!htmlContent) return null

          const parser = new DOMParser()
          const doc = parser.parseFromString(htmlContent, 'text/html')

          // Debug: log all h3.linktogtl elements
          const allGtlHeadings = doc.querySelectorAll('h3.linktogtl')
          // Look for the GTL Notes heading (not the "See GTL Notes" one)
          let gtlHeading = null

          for (const heading of allGtlHeadings) {
            const anchorTag = heading.querySelector('a')
            if (anchorTag) {
              const text = anchorTag.textContent.trim()
              const id = anchorTag.id

              // Look for the actual GTL Notes heading (usually has id="bookmark" and text starts with "GTL Notes")
              if (text.startsWith('GTL Notes') && id === 'bookmark') {
                gtlHeading = heading
                break
              }
              // Alternative: look for heading that doesn't contain brackets (not "[See GTL Notes]")
              else if (
                text.includes('GTL Notes') &&
                !text.includes('[') &&
                !text.includes(']')
              ) {
                gtlHeading = heading
                break
              }
            }
          }

          if (!gtlHeading) {
            // console.log('No GTL Notes heading found')
            return null
          }

          // Get the title from the anchor tag
          const anchorTag = gtlHeading.querySelector('a')
          const title = anchorTag ? anchorTag.textContent.trim() : 'GTL Notes'

          // Find all content after the GTL Notes heading
          let currentElement = gtlHeading.nextElementSibling
          const gtlContent = []

          // Get all following sibling elements until we hit another h3 or end of container
          while (currentElement) {
            if (currentElement.tagName === 'H3') {
              break
            }
            gtlContent.push(currentElement.outerHTML)
            currentElement = currentElement.nextElementSibling
          }
          return gtlContent.length > 0
            ? {
                title,
                content: gtlContent.join(''),
              }
            : null
        } catch (error) {
          console.warn('Error extracting GTL Notes:', error)
          return null
        }
      }, [])
      const removeGtlNotesFromContent = useCallback((htmlContent) => {
        if (!htmlContent) return htmlContent

        const parser = new DOMParser()
        const doc = parser.parseFromString(htmlContent, 'text/html')

        // Find the GTL Notes heading (same logic as extraction)
        const allGtlHeadings = doc.querySelectorAll('h3.linktogtl')
        let gtlHeading = null

        for (const heading of allGtlHeadings) {
          const anchorTag = heading.querySelector('a')
          if (anchorTag) {
            const text = anchorTag.textContent.trim()
            const id = anchorTag.id

            // Look for the actual GTL Notes heading
            if (text.startsWith('GTL Notes') && id === 'bookmark') {
              gtlHeading = heading
              break
            } else if (
              text.includes('GTL Notes') &&
              !text.includes('[') &&
              !text.includes(']')
            ) {
              gtlHeading = heading
              break
            }
          }
        }

        if (gtlHeading) {
          const elementsToRemove = [gtlHeading]

          // Remove all elements following the GTL Notes heading until next h3 or end
          let currentElement = gtlHeading.nextElementSibling
          while (currentElement) {
            if (currentElement.tagName === 'H3') {
              break
            }
            elementsToRemove.push(currentElement)
            currentElement = currentElement.nextElementSibling
          }

          // Remove all collected elements
          elementsToRemove.forEach((el) => el.remove())
        }

        return doc.body.innerHTML
      }, [])
      const memoizedGtlNotes = useMemo(() => {
        return articleContent?.content
          ? extractGtlNotes(articleContent.content)
          : null
      }, [articleContent?.content, extractGtlNotes])
      // * Here we are creating dynamic link tag for this particular component
      useEffect(() => {
        const environment = process.env.REACT_APP_ENVIRONMENT || 'dev'
        const link = document.createElement('link')
        link.rel = 'stylesheet'
        link.href = `https://gtlcdn-eufeh8ffbvbvacgf.z03.azurefd.net/guide/stylesheets/${environment}/article.css`
        link.id = 'external-css'

        if (!document.getElementById('external-css')) {
          document.head.appendChild(link)
        }

        return () => {
          document.getElementById('external-css')?.remove() // Cleanup on unmount
        }
      }, [])
      // SEO Data Generation
      const generateSEOData = useCallback(() => {
        if (!articleContent) return null

        // Extract law information from slug
        const countryCode = articleContent.slug?.split('-')[0] || 'uae'
        const country =
          countryCode === 'uae' ? 'UAE' : countryCode.toUpperCase()

        // Generate title based on article data
        const seoTitle = articleContent.title
          ? `${articleContent.title.replace(
              /<[^>]*>/g,
              ''
            )} | ${country} Tax Law | GCC Tax Laws`
          : `Article ${
              articleContent.number || ''
            } | ${country} Tax Law | GCC Tax Laws`

        // Generate description from content or use default
        const seoDescription =
          articleContent.summary ||
          (articleContent.textOnly
            ? articleContent.textOnly.substring(0, 155) + '...'
            : `Detailed analysis of ${
                articleContent.title || 'tax law article'
              } under ${country} tax legislation. Complete legal text and commentary.`)

        // Generate canonical URL
        const canonicalUrl = `https://gcctaxlaws.com/articles/${articleContent.slug}`

        // Generate keywords based on content
        const keywords = [
          `${country} tax law`,
          articleContent?.content?.toLowerCase().includes('definition')
            ? 'tax definitions'
            : null,
          articleContent.slug?.includes('cit') ? 'corporate income tax' : null,
          articleContent.slug?.includes('vat') ? 'value added tax' : null,
          'GCC tax laws',
          'tax compliance',
          `article ${articleContent.number}`,
        ]
          .filter(Boolean)
          .join(', ')

        // Generate schema markup
        const schemaMarkup = generateArticleSchema(articleContent)

        return {
          title: seoTitle,
          description: seoDescription,
          canonical: canonicalUrl,
          keywords,
          schemaMarkup,
          publishedDate: articleContent?.createdAt,
          modifiedDate: articleContent?.updatedAt,
          country,
          lawName: articleContent?.lawId,
        }
      }, [articleContent])

      // Fetch article content if needed
      useEffect(() => {
        const getArticleContent = async () => {
          // If we already have full article data, no need to fetch again
          if (article?.content) {
            setArticleContent(article)
            setLoading(false)
            return
          }

          // Skip if no slug or already fetching
          if (!article?.slug || fetchedRef.current) {
            return
          }

          // Check module-level cache first
          if (contentCache[article.slug]) {
            setArticleContent(contentCache[article.slug])
            setLoading(false)
            return
          }
          setLoading(true)
          fetchedRef.current = true

          try {
            // Use the service function
            // const data = await fetchArticleBySlug(article.slug)
            const data = await apiService.getArticleBySlug(article.slug)
            // Store in module-level cache
            contentCache[article.slug] = data
            setArticleContent(data)
            setError(null)
          } catch (err) {
            console.error('Error fetching article content:', err.message)
            setError(err.message)
            fetchedRef.current = false // Allow retry on error
          } finally {
            setLoading(false)
          }
        }

        getArticleContent()

        // Cleanup function
        return () => {
          fetchedRef.current = false
        }
      }, [article])

      // Generate SEO data
      const seoData = generateSEOData()

      // Parse HTML content when article content changes, so we can check whether it contains any tables or not, if yes then we can show sort button
      useEffect(() => {
        if (articleContent?.content) {
          // Parse the HTML content
          const parser = new DOMParser()
          const doc = parser.parseFromString(
            articleContent.content,
            'text/html'
          )
          setParsedContent(doc)
          setSortedContent(null) // Reset sorted content when article changes
          // Extract GTL Notes
          if (memoizedGtlNotes) {
            setGtlNotesContent(memoizedGtlNotes.content)
            setGtlNotesTitle(memoizedGtlNotes.title)

            // Remove GTL Notes from main content
            const contentWithoutGtl = removeGtlNotesFromContent(
              articleContent.content
            )
            setContentHtml(contentWithoutGtl)
          } else {
            setGtlNotesContent(null)
            setContentHtml(articleContent.content)
          }
        }
      }, [articleContent?.content, memoizedGtlNotes, removeGtlNotesFromContent])

      // below function is used to sort all the tables, we just need to sort the table where definitions are there in the article content
      const sortDefinitions = useCallback(() => {
        if (!parsedContent) return

        // Create a deep clone of the parsed content (without GTL Notes)
        const baseContent = memoizedGtlNotes
          ? removeGtlNotesFromContent(articleContent?.content || '')
          : articleContent?.content || ''

        const parser = new DOMParser()
        const doc = parser.parseFromString(baseContent, 'text/html')
        const docClone = doc.cloneNode(true)

        // Find all tables in the document
        // const tables = docClone.querySelectorAll('table')

        // Find only tables with class="definition"
        const tables = docClone.querySelectorAll('table.definition')

        tables.forEach((table) => {
          // Get all rows except header row (if any)
          const rows = Array.from(table.querySelectorAll('tr')).slice(1)

          if (rows.length > 0) {
            // Sort rows by the text in the first column
            rows.sort((a, b) => {
              const aText = a.querySelector('td:first-child')?.textContent || ''
              const bText = b.querySelector('td:first-child')?.textContent || ''
              return aText.localeCompare(bText)
            })

            // Remove existing rows
            rows.forEach((row) => row.parentNode.removeChild(row))

            // Append sorted rows
            const tbody = table.querySelector('tbody') || table
            rows.forEach((row) => tbody.appendChild(row))
          }
        })

        setSortedContent(docClone)
        setContentHtml(docClone.body.innerHTML)

        if (showSnackbar) {
          showSnackbar('Definitions sorted alphabetically', 'success')
        }
      }, [
        parsedContent,
        showSnackbar,
        memoizedGtlNotes,
        articleContent,
        removeGtlNotesFromContent,
      ])

      // Reset to original content
      const resetToDefault = useCallback(() => {
        setSortedContent(null)

        // Reset to content without GTL Notes
        const baseContent = memoizedGtlNotes
          ? removeGtlNotesFromContent(articleContent?.content || '')
          : articleContent?.content || ''

        setContentHtml(baseContent)

        // Provide feedback when resetting
        if (showSnackbar) {
          showSnackbar('Restored original order', 'info')
        }
      }, [
        articleContent,
        showSnackbar,
        memoizedGtlNotes,
        removeGtlNotesFromContent,
      ])

      const handleFeedbackFormToggle = useCallback(() => {
        setIsFeedbackFormOpen((prev) => !prev)
      }, [])
      const handleShare = useCallback(async () => {
        const url = `${window.location.origin}/articles/${
          articleContent?.slug || article?.slug
        }`
        const title = articleContent?.title || article?.title || 'Article'

        // Remove HTML tags from title for clean sharing
        const cleanTitle = title.replace(/<[^>]*>/g, '')

        const shareData = {
          title: cleanTitle,
          text: `Check out this article: ${cleanTitle}`,
          url: url,
        }

        try {
          // Check if Web Share API is supported (mainly mobile)
          if (navigator.share) {
            await navigator.share(shareData)
            if (showSnackbar) {
              showSnackbar('Article shared successfully! 📤', 'success')
            }
          } else {
            // Fallback to clipboard copy
            await navigator.clipboard.writeText(url)
            if (showSnackbar) {
              showSnackbar('Article link copied to clipboard! 📋', 'success')
            }
          }
        } catch (error) {
          if (error.name !== 'AbortError') {
            // Fallback to clipboard if share fails
            try {
              await navigator.clipboard.writeText(url)
              if (showSnackbar) {
                showSnackbar('Article link copied to clipboard! 📋', 'success')
              }
            } catch (clipboardError) {
              if (showSnackbar) {
                showSnackbar('Failed to share article', 'error')
              }
            }
          }
        }
      }, [articleContent, article, showSnackbar])

      const handlePrint = useCallback(() => {
        if (componentRef.current) {
          const wasAccordionExpanded = isAccordionExpanded

          // If GTL Notes exist and accordion is closed, expand it

          if (gtlNotesContent && !isAccordionExpanded) {
            setIsAccordionExpanded(true)

            // Wait for accordion to expand before printing

            setTimeout(() => {
              performPrint()

              // Restore original accordion state after printing

              setIsAccordionExpanded(wasAccordionExpanded)
            }, 300) // Give accordion time to expand
          } else {
            // No GTL Notes or accordion already expanded, print immediately

            performPrint()
          }
        }

        function performPrint() {
          const printWindow = window.open('', '_blank')
          printWindow.document.write('<html><head><title>GCC Tax Laws</title>')

          // Copy all styles from the current document to the print document
          const styleSheets = document.styleSheets
          for (let i = 0; i < styleSheets.length; i++) {
            if (styleSheets[i].href) {
              printWindow.document.write(
                `<link rel="stylesheet" type="text/css" href="${styleSheets[i].href}">`
              )
            } else {
              try {
                let css = ''
                for (let j = 0; j < styleSheets[i].cssRules.length; j++) {
                  css += styleSheets[i].cssRules[j].cssText
                }
                printWindow.document.write(`<style>${css}</style>`)
              } catch (e) {
                console.warn('Error accessing stylesheet', e)
              }
            }
          }

          printWindow.document.write('</head><body>')
          printWindow.document.write(componentRef.current.innerHTML)
          printWindow.document.write('</body></html>')
          printWindow.document.close()
          // printWindow.focus();
          printWindow.print()
          // Attach the onafterprint event to close the print window
          printWindow.onafterprint = () => {
            printWindow.close()
          }
        }
      }, [gtlNotesContent, isAccordionExpanded])

      // With this updated check that only looks for tables with class="definition":
      const hasDefinitionTables =
        parsedContent?.querySelectorAll('table.definition').length > 0

      // useEffect will work if you apply proper querySelector, for example you have written find all anchor tags who have target="_blank", but if you try to inspect the article in browser, you won't find the target attribute in anchor tag, maybe becuase of DOMPurify.sanitize()
      // Method 2 Trying html-react-parser -  avoiding potential memory leaks from improper handling of event listeners

      if (loading) {
        return (
          <Box sx={{ padding: 2, margin: 2 }}>
            <Skeleton variant='rectangular' width='100%' height={200} />
            <Skeleton variant='text' width='60%' />
            <Skeleton variant='text' width='80%' />
          </Box>
        )
      }
      if (error) {
        return (
          <Enhanced404Handler
            error={error}
            article={article}
            showFullDetails={process.env.REACT_APP_ENVIRONMENT === 'dev'} // Show details only in dev
          />
        )
      }
      return (
        <>
          {/* SEO Component - Only add if not in modal and we have content */}
          {!isInModal && seoData && (
            <SEO
              title={seoData.title}
              description={seoData.description}
              canonical={seoData.canonical}
              keywords={seoData.keywords}
              schemaMarkup={seoData.schemaMarkup}
              publishedDate={seoData.publishedDate}
              modifiedDate={seoData.modifiedDate}
              country={seoData.country}
              lawName={seoData.lawName}
              articleType='article'
            />
          )}
          {articleContent?.betaVersion && (
            <BetaVersionNotice
              show={articleContent.betaVersion}
              isInModal={isInModal}
            />
          )}
          <Paper
            ref={componentRef}
            // ref={ref}
            sx={{ padding: 2, margin: 2, borderRadius: '8px' }}
          >
            <Box display='flex' justifyContent='flex-end' alignItems='center'>
              <Box>
                {hasDefinitionTables && (
                  <Tooltip
                    title={
                      sortedContent
                        ? 'Restore the order of definitions back'
                        : 'Sort Definitions Alphabetically'
                    }
                    arrow
                  >
                    <IconButton
                      aria-label={
                        sortedContent
                          ? 'Restore the order of definitions back'
                          : 'Sort Definitions Alphabetically'
                      }
                      onClick={sortedContent ? resetToDefault : sortDefinitions}
                      size={isMobileScreen ? 'small' : 'medium'}
                    >
                      {sortedContent ? (
                        <RestoreRoundedIcon
                          sx={{ color: themeColors.primary }}
                        />
                      ) : (
                        <SortByAlphaRoundedIcon
                          sx={{ color: themeColors.primary, fontSize: 28 }}
                        />
                      )}
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip
                  title={
                    isFeedbackFormOpen
                      ? 'Close feedback form'
                      : 'Report Broken Links in this Article'
                  }
                  arrow
                >
                  <IconButton
                    aria-label={
                      isFeedbackFormOpen
                        ? 'Close feedback form'
                        : 'Report Broken Links in this Article'
                    }
                    onClick={handleFeedbackFormToggle}
                    size={isMobileScreen ? 'small' : 'medium'}
                  >
                    {isFeedbackFormOpen ? (
                      <CloseRoundedIcon color='error' />
                    ) : (
                      <ReportRoundedIcon color='error' />
                    )}
                  </IconButton>
                </Tooltip>
                <Tooltip title='Print this article' arrow>
                  <IconButton
                    aria-label='Print this article'
                    // title='Print this article'
                    onClick={handlePrint}
                    size={isMobileScreen ? 'small' : 'medium'}
                  >
                    <PrintRoundedIcon
                      // fontSize="large"
                      sx={{ color: themeColors.primary }}
                      // color='primary'
                    />
                  </IconButton>
                </Tooltip>
                <Tooltip title='Share this article' arrow>
                  <IconButton
                    aria-label='Share this article'
                    onClick={handleShare}
                    size={isMobileScreen ? 'small' : 'medium'}
                  >
                    <ShareRounded sx={{ color: themeColors.primary }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title='Mail this article' arrow>
                  <IconButton
                    aria-label='Mail this article'
                    // title='Mail this article'
                    onClick={() => handleEmail(articleContent || article)}
                    size={isMobileScreen ? 'small' : 'medium'}
                  >
                    <MailRoundedIcon
                      sx={{ color: themeColors.primary }}
                      // color='primary'
                    />
                  </IconButton>
                </Tooltip>
                <Tooltip title='Open this article in new tab' arrow>
                  <IconButton
                    aria-label='Open this article in new tab'
                    // title='Open this article in new tab'
                    onClick={() =>
                      handleOpenInNewTab(
                        articleContent || article,
                        `/articles/${article?.slug}`,
                        showSnackbar
                      )
                    }
                    size={isMobileScreen ? 'small' : 'medium'}
                  >
                    {/*fontSize: (default 24px), OR large, (default medium), small, string */}
                    <OpenInNewRoundedIcon
                      // sx={{ fontSize: 32 }}
                      sx={{ color: themeColors.primary }}
                      // color='primary'
                    />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            {isFeedbackFormOpen && (
              <FeedbackForm
                articleId={articleContent?.slug || article?.slug}
                onClose={handleFeedbackFormToggle}
                showSnackbar={showSnackbar} // Pass showSnackbar to FeedbackForm
              />
            )}
            {/* Display the parsed HTML content */}
            {contentHtml ? (
              <Box mt={2}>
                {parseHtmlSanitizeAddTargetToLinks(
                  contentHtml,
                  searchQuery,
                  '/articles/' + (articleContent?.slug || article?.slug),
                  {
                    fullDocumentContent: articleContent?.content || contentHtml,
                    documentTitle:
                      articleContent?.title || article?.title || 'Article',
                    showSnackbar: showSnackbar,
                  }
                )}
                {/* GTL Notes Accordion */}
                {gtlNotesContent && (
                  <Box mt={3}>
                    <Accordion
                      expanded={isAccordionExpanded}
                      onChange={(event, expanded) =>
                        setIsAccordionExpanded(expanded)
                      }
                      aria-label='GTL Notes section'
                      className='gtl-notes-accordion'
                      sx={{
                        '&:before': {
                          display: 'none',
                        },
                        boxShadow: 'none',
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: '8px !important',
                        '&:not(:last-child)': {
                          borderBottom: 0,
                        },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        className='gtl-notes-accordion-summary'
                        sx={{
                          backgroundColor:
                            theme.palette.mode === 'dark'
                              ? 'rgba(255, 255, 255, 0.05)'
                              : 'rgba(0, 0, 0, 0.03)',
                          borderRadius: '8px',
                          '&.Mui-expanded': {
                            borderBottomLeftRadius: 0,
                            borderBottomRightRadius: 0,
                          },
                        }}
                      >
                        <Typography
                          variant='h6'
                          sx={{
                            fontWeight: 600,
                            color: '#43af76', // Use your custom green color instead of themeColors.primary
                          }}
                        >
                          {gtlNotesTitle}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails
                        sx={{ padding: 2 }}
                        className='gtl-notes-accordion-details'
                      >
                        <Box className='gtl-notes-content'>
                          {parseHtmlSanitizeAddTargetToLinks(
                            gtlNotesContent,
                            searchQuery,
                            '/articles/' +
                              (articleContent?.slug || article?.slug),
                            {
                              fullDocumentContent:
                                articleContent?.content || gtlNotesContent,
                              documentTitle: gtlNotesTitle,
                              showSnackbar: showSnackbar,
                            }
                          )}
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  </Box>
                )}
              </Box>
            ) : (
              <Typography>No content available.</Typography>
            )}
            {!isInModal && (
              <Box
                sx={{
                  position: 'fixed',
                  bottom: 20,
                  right: 20,
                  zIndex: 1000,
                }}
              >
                <Zoom in={showBackToTop}>
                  {isMediumScreen ? (
                    <Fab
                      size='small'
                      aria-label='Scroll back to top'
                      onClick={scrollToTop}
                      sx={{
                        backgroundColor: themeColors.primary,
                        color: 'white',
                        '&:hover': {
                          backgroundColor: themeColors.hover,
                        },
                      }}
                    >
                      <KeyboardArrowUpIcon fontSize='small' />
                    </Fab>
                  ) : (
                    <Fab
                      variant='extended'
                      size='medium'
                      aria-label='Scroll back to top'
                      onClick={scrollToTop}
                      sx={{
                        backgroundColor: themeColors.primary,
                        color: 'white',
                        '&:hover': { backgroundColor: themeColors.hover },
                        paddingLeft: 2,
                        paddingRight: 2,
                        borderRadius: buttonBorderRadius,
                      }}
                    >
                      <KeyboardArrowUpIcon sx={{ mr: 1 }} />
                      <Typography
                        variant='button'
                        sx={{
                          fontWeight: 'bold',
                        }}
                      >
                        Back to Top
                      </Typography>
                    </Fab>
                  )}
                </Zoom>
              </Box>
            )}
          </Paper>
        </>
      )
    }
export default Article
